import { CalendarCheck, Undo2 } from "lucide-react";
import { ROUTES } from "@/app/routes";
import { pathMatches, resolveNavHref } from "@/shared/components/layout/nav-path";
import type { NavItem } from "@/shared/components/layout/sidebar-nav.types";

export type PayrollView = "runs" | "corrections" | "days";
export interface PayrollDestination extends NavItem {
  view?: PayrollView;
}

const routes = ROUTES.workspace;
const groups = [
  { title: "", paths: [routes.payrollRuns.path] },
  {
    title: "Pay preparation",
    paths: [
      routes.payrollSalaries.path,
      routes.payrollInputs.path,
      routes.variablePay.path,
      routes.recoverySchedules.path,
    ],
  },
  {
    title: "Follow-up and records",
    paths: [
      routes.settlements.path,
      routes.complianceDocuments.path,
      routes.payrollMoneyAudit.path,
    ],
  },
  { title: "Setup", paths: [routes.payrollSetup.path, routes.parallelRun.path] },
];

/** Explicit admin destinations exclude employee payslips and unrelated payroll URLs. */
export function isPayrollAdminPath(pathname: string, slug: string): boolean {
  if (pathname.endsWith("/print")) return false;
  return groups.some((group) => group.paths.some((path) => pathMatches(pathname, path, slug)));
}

export function resolvePayrollRunsView(search: {
  view?: "corrections" | "days";
  correctionId?: string;
  correctionStatus?: string;
  correctionCursor?: string;
  obligationStatus?: string;
  obligationCursor?: string;
}): PayrollView {
  if (search.correctionId) return "corrections";
  if (search.view) return search.view;
  if (search.correctionStatus || search.correctionCursor) return "corrections";
  if (search.obligationStatus || search.obligationCursor) return "days";
  return "runs";
}

/** Reuse the permission-gated module catalog as the authority for access. */
export function payrollSidebarGroups(catalog: NavItem | null) {
  const destinations = catalog?.children ?? [];
  const run = destinations.find((item) => item.to === routes.payrollRuns.path);
  return groups
    .map((group) => {
      const items: PayrollDestination[] = group.paths.flatMap((path) => {
        const item = destinations.find((candidate) => candidate.to === path);
        if (!item) return [];
        return [
          {
            ...item,
            label:
              path === routes.payrollInputs.path
                ? "Extras"
                : path === routes.payrollSetup.path
                  ? "Payroll settings"
                  : item.label,
            ...(path === routes.payrollRuns.path ? { view: "runs" as const } : {}),
          },
        ];
      });
      if (group.title === "Follow-up and records" && run) {
        items.unshift(
          { ...run, label: "Pay corrections", icon: Undo2, view: "corrections" },
          { ...run, label: "Days to check", icon: CalendarCheck, view: "days" },
        );
      }
      return { title: group.title, items };
    })
    .filter((group) => group.items.length > 0);
}

export function isPayrollDestinationActive(
  item: PayrollDestination,
  pathname: string,
  slug: string,
  view: PayrollView,
): boolean {
  const href = resolveNavHref(item.to, slug);
  if (!pathMatches(pathname, href, slug)) return false;
  if (!item.view) return true;
  return item.view === (pathname === href ? view : "runs");
}
